import sanitize from "../../lib/dompurify";
import { bus, loadedFrames, options, postMessage } from "../../internal";
import Card from "../../internal/lib/card";
import Events from "../../internal/lib/events";
import paymentFieldId from "../../internal/lib/payment-field-id";
import { json2css } from "../../internal/lib/styles";
import { IDictionary } from "../../internal/lib/util";
import EventEmitter from "../../lib/event-emitter";
import generateGuid from "../../lib/generate-guid";

import actionAccumulateDataAndTokenize from "./action-accumulate-data-and-tokenize";
import actionAddStylesheet from "./action-add-stylesheet";
import actionCardTrackButtonClick from "./action-card-track-button-click";
import actionPaymentRequestComplete from "./action-payment-request-complete";
import actionPaymentRequestStart from "./action-payment-request-start";
import actionRequestData from "./action-request-data";
import actionSetCardType from "./action-set-card-type";
import actionSetFocus from "./action-set-focus";
import actionSetPlaceholder from "./action-set-placeholder";
import actionSetRequired from "./action-set-required";
import actionSetText from "./action-set-text";
import actionSetValue from "./action-set-value";


export interface IFrameCollection {
  [key: string]: IframeField | undefined;
}

export const fieldTypeAutocompleteMap: IDictionary = {
  "card-cvv": "cc-csc",
  "card-expiration": "cc-exp",
  "card-number": "cc-number",
};

export class IframeField extends EventEmitter {
  public static register(type: string) {
    const query = window.location.hash.replace("#", "");
    const data: any = JSON.parse(atob(query));
    const id: string = data.id;
    const enableAutocomplete = data.enableAutocomplete || false;
    IframeField.createField(id, type, data.type, enableAutocomplete);
    IframeField.addMessageListener(id, type, data.targetOrigin);

    postMessage.post(
      {
        data: { type },
        id,
        type: "ui:iframe-field:register",
      },
      "parent",
    );
    IframeField.triggerResize(id);

    // Fix iOS issue with cross-origin iframes
    Events.addHandler(document.body, "touchstart", () => { /** */ });
  }

  /**
   * Creates the inner field within the iframe window and sets
   * any appropriate attributes, properties, and event handlers.
   * @param id Field ID
   * @param name Field type
   * @param type Type of element
   * @param enableAutocomplete Whether autocomplete should be enabled
   */
  public static createField(id: string, name: string, type: string, enableAutocomplete: boolean) {
    const input = document.createElement(
      type === "button" ? "button" : "input",
    );
    input.setAttribute("type", type === "button" ? "button" : (name === "card-holder-name" ? "text" : "tel"));
    input.id = paymentFieldId;
    input.className = name;
    input.setAttribute("data-id", id);

    if (enableAutocomplete === true && fieldTypeAutocompleteMap[name]) {
      input.setAttribute("autocomplete", fieldTypeAutocompleteMap[name]);
    } else {
      input.setAttribute("autocomplete", "off");
    }

    if (name === "card-track") {
      const message = "Read Card";
      input.appendChild(document.createTextNode(message));
    } else if (type === "button") {
      const message = "Submit";
      input.appendChild(document.createTextNode(message));
    }

    const dest = document.getElementById(paymentFieldId + "-wrapper");
    if (!dest) {
      return;
    }
    dest.insertBefore(input, dest.firstChild);

    IframeField.addFrameFocusEvent();
    if (enableAutocomplete === true && name === "card-number") {
        IframeField.createAutocompleteField(dest, id, "card-cvv", "cardCsc", "cc-csc");
        IframeField.createAutocompleteField(dest, id, "card-expiration", "cardExpiration", "cc-exp");
        IframeField.createAutocompleteField(dest, id, "card-holder-name", "cardHolderName", "cc-name");
      }

    if (name === "card-track") {
      Events.addHandler(input, "click", () => {
        actionCardTrackButtonClick(id);
      });
    } else if (type === "button") {
      if (name === "apple-pay-submit") {
        Events.addHandler(input, "click", () => {
          postMessage.post(
            {
              id,
              type: "ui:iframe-field:apple-pay-submit-click",
            },
            "parent",
          );
        });
      } else {
        Events.addHandler(input, "click", () => {
          postMessage.post(
            {
              id,
              type: "ui:iframe-field:click",
            },
            "parent",
          );
        });
      }
    }

    switch (name) {
      case "card-number":
        Card.attachNumberEvents("#" + input.id);
        input.name = "cardNumber";
        break;
      case "card-expiration":
        Card.attachExpirationEvents("#" + input.id);
        break;
      case "card-cvv":
        Card.attachCvvEvents("#" + input.id);
        break;
      default:
        break;
    }
  }

  /**
   * Appends a hidden input to the given destination to accept
   * full autocomplete/auto-fill data from the browser. The
   * parent window is also notified of data changes to these
   * fields in order display the new data to the end-user.
   *
   * @param destination Parent node for new element
   * @param id Field ID
   * @param type Field type
   * @param name Field name to be used
   * @param autocomplete Value for field's autocomplete attribute
   */
  public static createAutocompleteField(
    destination: Node,
    id: string,
    type: string,
    name: string,
    autocomplete: string,
  ) {
    const element = document.createElement("input");
    element.name = name;
    element.className = "autocomplete-hidden";
    element.tabIndex = -1;
    element.autocomplete = autocomplete;
    Events.addHandler(element, "input", () => {
      let value = element && element.value ? element.value : "";

      // this shouldn't happen, but explicitly ignore to prevent
      // these fields from leaking their data to the parent
      if (type === "card-number" || type === "account-number") {
        value = "";
      }

      postMessage.post(
        {
          data: {
            type,
            value,
          },
          id,
          type: "ui:iframe-field:set-autocomplete-value",
        },
        "parent",
      );
    });
    destination.appendChild(element);
  }

  /**
   * addFrameFocusEvent
   *
   * Ensures an iframe's document forwards its received focus
   * to the input field. Helps provide consistent behavior in
   * all browsers.
   */
  public static addFrameFocusEvent() {
    const element = document.getElementById(paymentFieldId);
    if (!element) {
      return;
    }

    const focusEventName = "focus";
    const handler = (e: any) => {
      if (e.fromElement === element) {
        return;
      }
      if (e.relatedTarget) {
        return;
      }

      element.focus();
    };

    if ((document as any)["on" + focusEventName + "in"]) {
      Events.addHandler(document, focusEventName + "in", handler);
    } else {
      Events.addHandler(document, focusEventName, handler);
    }
  }

  /**
   * Sets the iframe window's postMessage handler in order to
   * react to parent/sibling events.
   *
   * @param id Field ID
   * @param type Field type
   * @param targetOrigin Parent window's origin
   */
  public static addMessageListener(
    id: string,
    type: string,
    targetOrigin: string,
  ) {
// sanitize targetOrigin for XSS attacks
    const cleanedTargetOrigin = sanitize(targetOrigin);
// update the global statge with information about the parent window
    loadedFrames.parent = {
      frame: parent,
      url: cleanedTargetOrigin,
    } as any;

    postMessage.receive((data: any) => {
      if (!data.id || (data.id && data.id !== id)) {
        return;
      }

      const event: string = data.type.replace("ui:iframe-field:", "");

      switch (event) {
        case "accumulate-data":
          actionAccumulateDataAndTokenize(id, type, data);
          break;
        case "add-stylesheet":
          actionAddStylesheet(data.data.css);
          IframeField.triggerResize(id);
          break;
        case "payment-request-complete":
          actionPaymentRequestComplete(id, data);
          break;
        case "payment-request-start":
          actionPaymentRequestStart(id, data);
          break;
        case "request-data":
          actionRequestData(id, type, data);
          break;
        case "set-card-type":
          actionSetCardType(data.data.cardType);
          break;
        case "set-focus":
          actionSetFocus();
          break;
        case "set-placeholder":
          actionSetPlaceholder(data.data.placeholder);
          IframeField.triggerResize(id);
          break;
        case "set-text":
          actionSetText(data.data.text);
          IframeField.triggerResize(id);
          break;
        case "set-value":
          actionSetValue(data.data.value);
          IframeField.triggerResize(id);
          break;
        case "set-required":
          actionSetRequired(data.data.value);
          IframeField.triggerResize(id);
          break;
        case "update-options":
          for (const prop in data.data) {
            if (data.data.hasOwnProperty(prop)) {
              options[prop] = data.data[prop];
            }
          }
          break;
        default:
          break;
      }
    });
  }

  public static triggerResize(id: string) {
    postMessage.post(
      {
        data: {
          height: document.body.offsetHeight + 1,
        },
        id,
        type: "ui:iframe-field:resize",
      },
      "parent",
    );
  }

  public container: Element | null;
  public frame: HTMLIFrameElement;
  public id: string;
  public targetNode: any;
  public type: "button" | "input";
  public url: string;

  constructor(type: string, selector: string, src: string) {
    super();

    this.id = btoa(generateGuid());
    this.type = type === "submit" || type === "card-track" || type === "apple-pay-submit" ? "button" : "input";
    this.url = src;
    this.frame = this.makeFrame(type, this.id);
    this.frame.onload = () => {
      this.emit("load");
    };
    const cleanedTargetOrigin = sanitize(window.location.href);
    this.frame.src =
      src +
      "#" +
      btoa(
        JSON.stringify({
          enableAutocomplete: options.enableAutocomplete,
          id: this.id,
          targetOrigin: cleanedTargetOrigin,
          type: this.type,
        }),
      );

    this.container = document.querySelector(selector);

    if (!this.container) {
      bus.emit("error", {
        error: true,
        reasons: [
          {
            code: "ERROR",
            message: "IframeField: target cannot be found with given selector",
          },
        ],
      });
      return;
    }

    this.container.appendChild(this.frame);
    this.on("dispose", () => {
      loadedFrames[this.id] = undefined;
      if (this.container) {
        this.container.removeChild(this.frame);
      }
    });

    postMessage.receive((data: any) => {
      if (!data.id || (data.id && data.id !== this.id)) {
        return;
      }

      const event: string = data.type.replace("ui:iframe-field:", "");

      switch (event) {
        case "register":
          postMessage.post(
            {
              data: options,
              id: this.id,
              type: "ui:iframe-field:update-options",
            },
            this.id,
          );
          break;
        case "resize":
          this.frame.style.height = `${data.data.height}px`;
          break;
        case "pass-data":
          postMessage.post(
            {
              data: {
                required: data?.data?.required || false,
                type: data.data.type,
                value: data.data.value,
              },
              id: data.data.target,
              type: "ui:iframe-field:accumulate-data",
            },
            data.data.target,
          );
          break;
        default:
          break;
      }

      this.emit(event, data.data);
    });

    loadedFrames[this.id] = this;
  }

  public addStylesheet(json: IDictionary) {
    const css = json2css(json);
    postMessage.post(
      {
        data: { css },
        id: this.id,
        type: "ui:iframe-field:add-stylesheet",
      },
      this.id,
    );
  }

  public setFocus() {
    postMessage.post(
      {
        id: this.id,
        type: "ui:iframe-field:set-focus",
      },
      this.id,
    );
  }

  public setPlaceholder(placeholder: string) {
    postMessage.post(
      {
        data: { placeholder },
        id: this.id,
        type: "ui:iframe-field:set-placeholder",
      },
      this.id,
    );
  }

  public setText(text: string) {
    postMessage.post(
      {
        data: { text },
        id: this.id,
        type: "ui:iframe-field:set-text",
      },
      this.id,
    );
  }

  public setValue(value: string) {
    postMessage.post(
      {
        data: { value },
        id: this.id,
        type: "ui:iframe-field:set-value",
      },
      this.id,
    );
  }

  public setRequired(value: string) {
    postMessage.post(
      {
        data: { value },
        id: this.id,
        type: "ui:iframe-field:set-required",
      },
      this.id,
    );
  }

  private makeFrame(type: string, id: string) {
    const frame = document.createElement("iframe");
    frame.id = `secure-payment-field-${type}-${id}`;
    frame.name = type;
    frame.style.border = "0";
    frame.frameBorder = "0";
    frame.scrolling = "no";
    frame.setAttribute("allowtransparency", "true");
    frame.allowPaymentRequest = true;
    return frame;
  }
}
